import { FileView } from 'gallerydigitalassets-v1-client'
import { all, takeLatest, call, put, select } from 'redux-saga/effects'
import delay from '@redux-saga/delay-p'
import {
  addToast,
  IAsset,
  IUser,
  ICreateAssetFileValues,
  deleteFileAPI,
  processError,
  PageStatus,
  removeToast,
  IAssetActivity,
  getAssetsByCollectionIdApi,
  setSubcategoryPageAssetsPaginationIsLoading,
  getSubcategoryAssetsApi,
  appendSubcategoryPageAssets,
  setSubcategoryPageAssets,
  ISearchSort,
  setSearchPageAssetsPaginationIsLoading,
  appendSearchPageAssets,
  setSearchPageAssets,
  searchSelfAssetToggleSelector,
} from 'store'

import {
  getAssetApi,
  createAssetApi,
  editAssetApi,
  deleteAssetApi,
  bulkDeleteAssetApi,
  getAssetUsersApi,
  addAssetUserApi,
  deleteAssetUserApi,
  addFileToAssetApi,
  downloadAssetApi,
  downloadAssetsApi,
  downloadLinkedAssetsApi,
  getAssetActivityApi,
  process3DFileApi,
  getAssetReportsApi,
  createAssetReport,
  resolveAssetReport,
} from './api'
import {
  getAssetReducer,
  setAssetReducer,
  createAssetReducer,
  addAssetFileStatusReducer,
  setAssetFileStatusValueReducer,
  clearAssetFileStatusValuesReducer,
  editAssetReducer,
  addFilesToAssetReducer,
  deleteAssetReducer,
  bulkDeleteAssetReducer,
  getAssetUsersReducer,
  setAssetUsersReducer,
  addAssetUserReducer,
  deleteAssetUserReducer,
  downloadAssetReducer,
  downloadAssetsReducer,
  downloadLinkedAssetReducer,
  setAssetDetailPageStatus,
  setAssetActivityReducer,
  getAssetActivityReducer,
  setAssetActivityIsLoading,
  process3DFileApiReducer,
  setAssetReportsIsLoading,
  setAssetReportsReducer,
  getAssetReportsReducer,
  createAssetReportReducer,
  resolveAssetReportReducer,
} from './slice'
import { STORAGE_KEYS } from '../../config'
import { AssetReportView } from '@galleryjs/api-digital-assets'
import {
  appendCollectionAssetsState,
  setCollectionAssetsPaginationIsLoading,
  setCollectionAssetsState,
  setFeaturedCollectionAssets,
  setFeaturedCollectionsIsLoading,
} from 'store/collections/slice'
import { searchAssetsApi } from 'store/search/api'

export function* getAssetSaga(data: any) {
  yield put(setAssetDetailPageStatus(PageStatus.loading))
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    const asset: IAsset = yield call(getAssetApi, xTgtLanId, data.payload)
    yield put(setAssetReducer(asset))
    yield put(setAssetDetailPageStatus(PageStatus.success))
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
    yield put(setAssetDetailPageStatus(PageStatus.error))
  }
}

// methods for bulk updating file status on asset create
function* addFileToAssetStatusSaga(data: any) {
  const { file, name } = data
  yield put(addAssetFileStatusReducer({ name, file }))
}

function* updateFileToAssetStatusSaga(data: any) {
  const { assetId, file, name, callTranscoding, taxonomyId } = data
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  yield put(setAssetFileStatusValueReducer({ name, status: 'UPLOADING' }))
  yield call(addFileToAssetApi, xTgtLanId, assetId, file, name, callTranscoding, taxonomyId)
  yield put(setAssetFileStatusValueReducer({ name, status: 'UPLOADED' }))
}

function* createAssetSaga(data: any) {
  // clear previous upload status data
  yield put(clearAssetFileStatusValuesReducer())
  const { name, files, isAssetPublic, taxonomyId, onSuccessCallback, onErrorCallback } = data.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  let assetFile: ICreateAssetFileValues = files[0]
  let filesToUpload: ICreateAssetFileValues[] = []
  let startPipelineOnAssetCreate = 'true'

  if (files.length > 1) {
    startPipelineOnAssetCreate = 'false'
    filesToUpload = files.slice(1)
    const fileCalls = files.map((file: ICreateAssetFileValues) => {
      return call(addFileToAssetStatusSaga, { name: file.name, file: file.file })
    })
    yield all(fileCalls)
  }

  try {
    // calls createApi with first file
    yield put(setAssetFileStatusValueReducer({ name: assetFile.name, status: 'UPLOADING' }))
    const asset: IAsset = yield call(
      createAssetApi,
      xTgtLanId,
      name,
      assetFile.file,
      isAssetPublic,
      taxonomyId,
      'NONE',
      startPipelineOnAssetCreate
    )
    yield put(setAssetFileStatusValueReducer({ name: assetFile.name, status: 'UPLOADED' }))
    const lastFilesToUpload = filesToUpload.splice(-1)
    // uploads subsequent files
    if (filesToUpload.length) {
      const fileCalls = filesToUpload.map(file => {
        return call(updateFileToAssetStatusSaga, {
          assetId: asset.id,
          file: file.file,
          name: file.name,
          callTranscoding: false,
          taxonomyId,
        })
      })
      yield all(fileCalls)
    }
    if (lastFilesToUpload.length) {
      const fileCalls = lastFilesToUpload.map(file => {
        return call(updateFileToAssetStatusSaga, {
          assetId: asset.id,
          file: file.file,
          name: file.name,
          callTranscoding: true,
          taxonomyId,
        })
      })
      yield all(fileCalls)
    }
    if (onSuccessCallback !== undefined) {
      yield call(onSuccessCallback, asset.id)
    }
    yield put(addToast({ color: 'success', headingText: 'Success', message: 'New asset created' }))
  } catch (error) {
    if (onErrorCallback !== undefined) {
      yield call(onErrorCallback)
    }
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* editAssetSaga(data: any) {
  const { assetId, editRequest, filesToAdd, filesToRemove, onSuccess } = data.payload
  const { initialAssetName, newAssetName, initialIsPublic, newIsPublic, initialTaxonomyId, newTaxonomyId } = editRequest
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    // make edit asset call if any data changes were made
    if (initialAssetName !== newAssetName || initialIsPublic !== newIsPublic || initialTaxonomyId !== newTaxonomyId) {
      yield call(editAssetApi, xTgtLanId, assetId, {
        assetName: newAssetName,
        makePublic: newIsPublic,
        taxonomyId: newTaxonomyId,
      })
    }
    // add and update redux state, and add files to asset if any files were added in modal
    if (filesToAdd.length) {
      yield call(addFilesToAssetSaga, { assetId, files: filesToAdd, taxonomyId: newTaxonomyId })
    }
    if (filesToRemove.length) {
      const fileIds = filesToRemove.map((file: FileView) => {
        return file.id
      })
      yield call(deleteFileAPI, xTgtLanId, fileIds, assetId)
    }
    yield call(onSuccess)
    yield call(getAssetSaga, { payload: assetId })
    yield put(
      addToast({
        color: 'success',
        headingText: 'Success',
        message: 'Asset Successfully Updated',
      })
    )
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

// TODO: ONCE ADD AND REMOVE FILE ENDPOINTS DONE, MOVE ALL INTO EDIT ASSET SAGA WITH ONE CALL FROM MODAL
function* addFilesToAssetSaga(data: any) {
  // clear previous upload status data
  yield put(clearAssetFileStatusValuesReducer())
  const { assetId, files, taxonomyId } = data
  try {
    // add files to status reducer for status indicator in modal
    const fileAddCalls = files.map((file: ICreateAssetFileValues) => {
      return call(addFileToAssetStatusSaga, { name: file.name, file: file.file })
    })
    yield all(fileAddCalls)
    const fileUpdateCalls = files.map((file: any, i: any) => {
      return call(updateFileToAssetStatusSaga, {
        assetId,
        file: file.file,
        name: file.name,
        callTranscoding: true,
        taxonomyId,
      })
    })
    yield all(fileUpdateCalls)
    yield put(getAssetReducer)
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* deleteAssetSaga(data: any) {
  const { id, pathname, navigate } = data.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    yield call(deleteAssetApi, xTgtLanId, id)
    yield put(
      addToast({
        color: 'success',
        headingText: 'Asset Deleted',
        message: `The ${id} asset has been successfully deleted.`,
      })
    )
    // Redirect to homepage if deeplinked to page, else go back to previous page
    if (!pathname) {
      yield call(navigate, '/')
    } else {
      yield call(navigate, -1)
    }
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* bulkDeleteAssetSaga(data: any): Generator<any, void, any> {
  const page = 0
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  const {
    assetIds,
    softDelete,
    galleryId,
    isHomepage,
    size,
    taxonomyId,
    querySearchCriteria,
    SEARCH_API_KEY,
    searchFunkyFilters,
    searchServer,
  } = data.payload
  const { q: searchQuery, p: searchPage, f: searchFilters, t: searchTaxonomy, size: searchSize } = querySearchCriteria
  const selfAssetToggle: boolean = yield select(searchSelfAssetToggleSelector)
  const searchUserList = selfAssetToggle ? [xTgtLanId] : []
  if (assetIds.length) {
    try {
      yield call(bulkDeleteAssetApi, xTgtLanId, assetIds, softDelete)
      // These endpoints are called reload the respective pages after assets are deleted
      // Collection endpoint for Homepage and Selected Collection Page
      if (galleryId) {
        if (isHomepage && xTgtLanId) {
          // Homepage Collection Page
          yield put(setFeaturedCollectionsIsLoading(true))
          const collectionAssets: { page: number; size: number; totalAssets: number; assets: IAsset[] } = yield call(
            getAssetsByCollectionIdApi,
            xTgtLanId,
            galleryId,
            page,
            size
          )
          yield put(setFeaturedCollectionsIsLoading(false))
          yield put(setFeaturedCollectionAssets(collectionAssets))
        } else {
          // Selected Collection Page
          yield put(setCollectionAssetsPaginationIsLoading(true))
          const collectionAssets: { page: number; size: number; totalAssets: number; assets: IAsset[] } = yield call(
            getAssetsByCollectionIdApi,
            xTgtLanId,
            galleryId,
            page,
            size
          )
          if (!page) {
            yield put(setCollectionAssetsState(collectionAssets))
          } else {
            yield put(appendCollectionAssetsState(collectionAssets))
          }
          yield put(setCollectionAssetsPaginationIsLoading(false))
        }
      } else if (taxonomyId) {
        // Collection endpoint for Subcategory Page (Selected taxonomoy)
        yield put(setSubcategoryPageAssetsPaginationIsLoading(true))
        const response: { taxonomyAssets: IAsset[]; totalCount: number } = yield call(
          getSubcategoryAssetsApi,
          xTgtLanId,
          taxonomyId,
          page
        )
        if (data.payload.page > 0) {
          yield put(
            appendSubcategoryPageAssets({
              assets: response.taxonomyAssets,
              page: page,
              totalCount: response.totalCount,
            })
          )
        } else {
          yield put(setSubcategoryPageAssets({ assets: response.taxonomyAssets, totalCount: response.totalCount }))
        }
        yield put(setSubcategoryPageAssetsPaginationIsLoading(false))
      } else {
        // Collection endpoint for Search Page
        yield put(setSearchPageAssetsPaginationIsLoading(true))
        const response: { searchAssets: IAsset[]; totalCount: number; sort: ISearchSort[] } = yield call(
          searchAssetsApi,
          searchQuery,
          searchPage,
          searchFilters,
          searchSize,
          [],
          searchFunkyFilters,
          searchServer,
          SEARCH_API_KEY,
          searchTaxonomy,
          undefined,
          searchUserList,
          false
        )
        if (page > 0) {
          yield put(
            appendSearchPageAssets({
              assets: response.searchAssets,
              page: data.payload.page,
              total: response.totalCount,
              sort: response.sort,
            })
          )
        } else {
          yield put(
            setSearchPageAssets({ assets: response.searchAssets, total: response.totalCount, sort: response.sort })
          )
        }
        yield put(setSearchPageAssetsPaginationIsLoading(false))
      }
      yield put(
        addToast({
          color: 'success',
          headingText: assetIds.length === 1 ? 'Asset Deleted' : 'Assets Deleted',
          message: `${assetIds.length} ${assetIds.length === 1 ? 'asset has' : 'assets have'} been successfully deleted.`,
        })
      )
    } catch (error) {
      if (error instanceof Error) {
        yield call(processError, error, error.message)
      } else {
        console.log(error)
      }
    }
  }
}

function* getAssetUsersSaga(data: any) {
  try {
    const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
    const assetUsers: IUser[] = yield call(getAssetUsersApi, xTgtLanId, data.payload.assetId, data.payload.permission)
    yield put(setAssetUsersReducer(assetUsers))
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* addAssetUserSaga(data: any) {
  try {
    const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
    yield call(addAssetUserApi, xTgtLanId, data.payload.assetId, data.payload.userId, data.payload.access)
    yield put(addToast({ color: 'success', headingText: 'Success', message: 'Asset manager updated' }))
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* deleteAssetUserSaga(data: any) {
  const { assetId, userId } = data.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    yield call(deleteAssetUserApi, xTgtLanId, assetId, userId)
    yield put(
      addToast({
        color: 'success',
        headingText: 'Success',
        message: 'Asset manager updated',
      })
    )
    yield call(getAssetUsersSaga, { payload: { assetId: assetId, permission: 'EDIT' } })
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  }
}

function* downloadAssetSaga(data: any) {
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  const { assetId } = data.payload
  const downloadToast = {
    color: 'success',
    headingText: 'Asset Downloading',
    message: 'Asset Download Initiated, Please Wait',
    autoHideDuration: 10000,
  }
  try {
    yield put(addToast(downloadToast))
    yield call(downloadAssetApi, xTgtLanId, assetId)
  } catch (error) {
    yield call(downloadAssetsSaga, { payload: { assetIds: [assetId] } })
    if (error.isAxiosError) {
      const errorResponse = { message: `${error.response.statusText}` }
      const errorMessage = errorResponse.message + ' Asset async download triggered.'
      yield call(processError, error, errorMessage)
    } else if (error instanceof Error) {
      yield call(processError, error, error.message + ' Asset async download triggered.')
    } else {
      console.log(error)
    }
  } finally {
    yield put(removeToast(downloadToast))
  }
}

function* downloadLinkedAssetSaga(data: any) {
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  const { assetId } = data.payload
  const downloadToast = {
    color: 'success',
    headingText: 'Linked Asset(s) Downloading',
    message: 'Download Initiated. Files will be sent via email.',
    autoHideDuration: 9000,
  }
  try {
    yield put(addToast(downloadToast))
    yield call(downloadLinkedAssetsApi, xTgtLanId, assetId)
  } catch (error) {
    if (error.isAxiosError) {
      const errorResponse = { message: `${error.response.statusText}` }
      const errorMessage = errorResponse.message
      yield call(processError, error, errorMessage)
    } else if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  } finally {
    yield put(removeToast(downloadToast))
  }
}

function* downloadAssetsSaga(data: any) {
  const { assetIds } = data.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  const downloadToast = {
    color: 'success',
    headingText: 'Assets downloading',
    message: 'Assets will be sent to your email when completed.',
    autoHideDuration: 10000,
  }
  try {
    yield put(addToast(downloadToast))
    yield call(downloadAssetsApi, xTgtLanId, assetIds)
    yield delay(6000)
  } catch (error) {
    if (error.isAxiosError) {
      const errorResponse = { message: `${error.response.statusText}` }
      const errorMessage = errorResponse.message
      yield call(processError, error, errorMessage)
    } else if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
  } finally {
    yield put(removeToast(downloadToast))
  }
}

function* getAssetActivitySaga(action: any) {
  const { assetActivityServer, assetId } = action.payload
  try {
    yield put(setAssetActivityIsLoading(true))
    const assetActivity: IAssetActivity = yield call(getAssetActivityApi, assetActivityServer, assetId)
    yield put(setAssetActivityReducer(assetActivity))
    yield put(setAssetActivityIsLoading(false))
  } catch (error) {
    if (error instanceof Error) {
      yield put(setAssetActivityIsLoading(false))
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
    yield put(setAssetDetailPageStatus(PageStatus.error))
  }
}

function* getAssetReportsSaga(action: any) {
  const { assetId } = action.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    yield put(setAssetReportsIsLoading(true))
    const assetReports: AssetReportView[] = yield call(getAssetReportsApi, assetId, xTgtLanId)
    yield put(setAssetReportsReducer(assetReports.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))))
    yield put(setAssetReportsIsLoading(false))
  } catch (error) {
    if (error instanceof Error) {
      yield put(setAssetReportsIsLoading(false))
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
    yield put(setAssetDetailPageStatus(PageStatus.error))
  }
}

function* createAssetReportSaga(action: any) {
  const { createAssetReportRequest, onSuccess, onError } = action.payload || {}
  const { assetId } = createAssetReportRequest || {}
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    const createdAssetReport: AssetReportView = yield call(createAssetReport, createAssetReportRequest, xTgtLanId)
    if (createdAssetReport.id) {
      yield put(setAssetReportsIsLoading(true))
      const assetReports: AssetReportView[] = yield call(getAssetReportsApi, assetId, xTgtLanId)
      yield put(setAssetReportsReducer(assetReports.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))))
      yield put(setAssetReportsIsLoading(false))
      yield call(onSuccess)
    } else {
      yield call(onError)
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(setAssetReportsIsLoading(false))
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
    yield put(setAssetDetailPageStatus(PageStatus.error))
  }
}

function* resolveAssetReportSaga(action: any) {
  const { resolveAssetReportPayload, onSuccess, onError } = action.payload || {}
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    const resolvedAssetReport: AssetReportView = yield call(resolveAssetReport, resolveAssetReportPayload, xTgtLanId)
    if (resolvedAssetReport.assetId) {
      yield put(setAssetReportsIsLoading(true))
      const assetReports: AssetReportView[] = yield call(getAssetReportsApi, resolvedAssetReport.assetId, xTgtLanId)
      yield put(setAssetReportsReducer(assetReports.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))))
      yield put(setAssetReportsIsLoading(false))
      yield call(onSuccess)
    } else {
      yield call(onError)
    }
  } catch (error) {
    if (error instanceof Error) {
      yield put(setAssetReportsIsLoading(false))
      yield call(processError, error, error.message)
    } else {
      console.log(error)
    }
    yield put(setAssetDetailPageStatus(PageStatus.error))
  }
}

function* process3DFileApiSaga(data: any) {
  const { assetId, pipelineId } = data.payload
  const xTgtLanId: string = localStorage.getItem(STORAGE_KEYS.TGT_LAN_ID)!!
  try {
    yield call(process3DFileApi, xTgtLanId, assetId, pipelineId)
  } catch (error) {
    if (error instanceof Error) {
      yield call(processError, error, error.message)
    } else {
      console.error(error)
    }
  }
}

export default function* watchAssetSagas() {
  yield all([
    takeLatest(getAssetReducer.type, getAssetSaga),
    takeLatest(createAssetReducer.type, createAssetSaga),
    takeLatest(editAssetReducer.type, editAssetSaga),
    takeLatest(deleteAssetReducer.type, deleteAssetSaga),
    takeLatest(bulkDeleteAssetReducer.type, bulkDeleteAssetSaga),
    takeLatest(getAssetUsersReducer.type, getAssetUsersSaga),
    takeLatest(addAssetUserReducer.type, addAssetUserSaga),
    takeLatest(deleteAssetUserReducer.type, deleteAssetUserSaga),
    takeLatest(addFilesToAssetReducer.type, addFilesToAssetSaga),
    takeLatest(downloadAssetReducer.type, downloadAssetSaga),
    takeLatest(downloadAssetsReducer.type, downloadAssetsSaga),
    takeLatest(downloadLinkedAssetReducer.type, downloadLinkedAssetSaga),
    takeLatest(getAssetActivityReducer.type, getAssetActivitySaga),
    takeLatest(getAssetReportsReducer.type, getAssetReportsSaga),
    takeLatest(createAssetReportReducer.type, createAssetReportSaga),
    takeLatest(resolveAssetReportReducer.type, resolveAssetReportSaga),
    takeLatest(process3DFileApiReducer.type, process3DFileApiSaga),
  ])
}
